import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import Fieldset from '../../Common/Components/Fieldset'
import TextInput from '../../Common/Components/TextInput'
import FormRow from '../../Common/Components/FormRow'
import Button from '../../Common/Components/Button'

import { LanguageContext } from '../../Common/Contexts'
import TabActionPromptContext from '../../Common/Components/Tabs/TabActionPromptContext'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { AbandonedTimeStatisticLanguage } from '../../../../i18n'

/**
 * Component properties type.
 */
type Props = {
    callsInAbndThres1: number | undefined
    setCallsInAbndThres1: (newValue: number | undefined) => void
    callsInAbndThres2: number | undefined
    setCallsInAbndThres2: (newValue: number | undefined) => void
    callsInAbndThres3: number | undefined
    setCallsInAbndThres3: (newValue: number | undefined) => void
    callsInAbndThres4: number | undefined
    setCallsInAbndThres4: (newValue: number | undefined) => void
    callsInAbndThres5: number | undefined
    setCallsInAbndThres5: (newValue: number | undefined) => void
    callsInAbndThres6: number | undefined
    setCallsInAbndThres6: (newValue: number | undefined) => void
    resetAbandonedTargets: () => void
}

/**
 * Answer performance section of SLA/KPI tab in report configuration modal window.
 *
 * @param callsInAnsThres1 Report property.
 * @param setCallsInAnsThres1 Setter for corresponding report property.
 * @param callsInAnsThres2 Report property.
 * @param setCallsInAnsThres2 Setter for corresponding report property.
 * @param callsInAnsThres3 Report property.
 * @param setCallsInAnsThres3 Setter for corresponding report property.
 * @param callsInAnsThres4 Report property.
 * @param setCallsInAnsThres4 Setter for corresponding report property.
 * @param callsInAnsThres5 Report property.
 * @param setCallsInAnsThres5 Setter for corresponding report property.
 * @param callsInAnsThres6 Report property.
 * @param setCallsInAnsThres6 Setter for corresponding report property.
 * @param resetAbandonedTargets Resets answer targets.
 */
const AbandonedTimeStatistic: React.FC<Props> = ({
    callsInAbndThres1,
    setCallsInAbndThres1,
    callsInAbndThres2,
    setCallsInAbndThres2,
    callsInAbndThres3,
    setCallsInAbndThres3,
    callsInAbndThres4,
    setCallsInAbndThres4,
    callsInAbndThres5,
    setCallsInAbndThres5,
    callsInAbndThres6,
    setCallsInAbndThres6,
    resetAbandonedTargets
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('AbandonedTimeStatisticLanguage')
    const { displayPrompt } = useContext(TabActionPromptContext)

    const handleAbandonedThresholdsReset = (): void => {
        displayPrompt(
            language.get(AbandonedTimeStatisticLanguage.resetAnswerTargetsPrompt),
            language.get(AbandonedTimeStatisticLanguage.resetAnswerTargetsConfirm),
            language.get(AbandonedTimeStatisticLanguage.resetAnswerTargetsCancel),
            resetAbandonedTargets,
            undefined
        )
    }

    return (
        <Fieldset className='abandoned-intervals-section'>
            <Heading level={HeadingLevel.TWO}>{language.get(AbandonedTimeStatisticLanguage.sectionHeader)}</Heading>
            <P>{language.get(AbandonedTimeStatisticLanguage.sectionPara)}</P>
            <FormRow isInline className={'target'}>
                <TextInput
                    value={callsInAbndThres1}
                    onChange={(value: string): void => setCallsInAbndThres1(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AbandonedTimeStatisticLanguage.target1Label)}
                    placeholder={language.get(AbandonedTimeStatisticLanguage.target1Placeholder)}
                    adornmentEnd={language.get(AbandonedTimeStatisticLanguage.targetUnits)}
                />
                <TextInput
                    value={callsInAbndThres2}
                    onChange={(value: string): void => setCallsInAbndThres2(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AbandonedTimeStatisticLanguage.target2Label)}
                    placeholder={language.get(AbandonedTimeStatisticLanguage.target2Placeholder)}
                    adornmentEnd={language.get(AbandonedTimeStatisticLanguage.targetUnits)}
                />
                <TextInput
                    value={callsInAbndThres3}
                    onChange={(value: string): void => setCallsInAbndThres3(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AbandonedTimeStatisticLanguage.target3Label)}
                    placeholder={language.get(AbandonedTimeStatisticLanguage.target3Placeholder)}
                    adornmentEnd={language.get(AbandonedTimeStatisticLanguage.targetUnits)}
                />
            </FormRow>
            <FormRow isInline className={'target'}>
                <TextInput
                    value={callsInAbndThres4}
                    onChange={(value: string): void => setCallsInAbndThres4(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AbandonedTimeStatisticLanguage.target4Label)}
                    placeholder={language.get(AbandonedTimeStatisticLanguage.target4Placeholder)}
                    adornmentEnd={language.get(AbandonedTimeStatisticLanguage.targetUnits)}
                />
                <TextInput
                    value={callsInAbndThres5}
                    onChange={(value: string): void => setCallsInAbndThres5(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AbandonedTimeStatisticLanguage.target5Label)}
                    placeholder={language.get(AbandonedTimeStatisticLanguage.target5Placeholder)}
                    adornmentEnd={language.get(AbandonedTimeStatisticLanguage.targetUnits)}
                />
                <TextInput
                    value={callsInAbndThres6}
                    onChange={(value: string): void => setCallsInAbndThres6(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AbandonedTimeStatisticLanguage.target6Label)}
                    placeholder={language.get(AbandonedTimeStatisticLanguage.target6Placeholder)}
                    adornmentEnd={language.get(AbandonedTimeStatisticLanguage.targetUnits)}
                />
            </FormRow>
            <Button
                label={language.get(AbandonedTimeStatisticLanguage.resetAnswerTargetsButtonLabel)}
                onClick={handleAbandonedThresholdsReset}
                isOutlined={false}
            />
        </Fieldset>
    )
}

export default React.memo(AbandonedTimeStatistic)
